type RouteItem = {
  path: string;
  name: string;
  children?: Record<string, RouteItem>;
};

type NestedRouteItem<T extends Record<string, RouteItem>> = {
  ROOT: string;
} & T;

const buildNestedRoutes = <T extends Record<string, RouteItem>>(
  parentPath: string,
  routes: T,
): NestedRouteItem<T> => {
  const nestedRoutes: NestedRouteItem<T> = {
    ROOT: parentPath,
    ...routes,
  } as NestedRouteItem<T>;

  return new Proxy(nestedRoutes, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, receiver);

      if (typeof value === 'object' && 'path' in value) {
        return {
          ...(value as RouteItem),
          path: `${parentPath}/${value.path}`,
          children: value.children
            ? buildNestedRoutes(`${parentPath}/${value.path}`, value.children)
            : undefined,
        };
      }

      return value;
    },
  });
};

export const ROUTES = {
  LOGIN: { path: '/', name: 'Login' },
  REGISTER_STAFF: { path: '/register-staff', name: 'Register Staff' },
  ACCOUNT_INFO: buildNestedRoutes('/account-info', {
    INFO_INQUIRY: {
      path: 'information-inquiry',
      name: 'Information Inquiry',
    },
    CHANGE_PASSWORD: {
      path: 'change-password',
      name: 'Change Password',
    },
  }),
  CUSTOMER_DELIVERY_FILES: buildNestedRoutes('/customer-delivery-files', {
    REGISTER_FILE: {
      path: 'register-file',
      name: 'Register File',
    },
    UPDATE_FILE: {
      path: 'update',
      name: 'Update File',
    },
  }),
  EXTERNAL_DELIVERY_FILES: buildNestedRoutes('/external-delivery-files', {
    REGISTER_FILE: {
      path: 'register-file',
      name: 'Register File',
    },
    UPDATE_FILE: {
      path: 'update',
      name: 'Update File',
    },
  }),
  EMPLOYEE_MANAGEMENT: buildNestedRoutes('/employee-management', {
    UPDATE_FILE: {
      path: 'update',
      name: 'Update Employee',
    },
  }),

  OPERATION_HISTORY: buildNestedRoutes('/operation-history', {
    CUSTOMER_DELIVERY_DOWNLOAD_FILES: {
      path: 'customer-delivery-download-files',
      name: 'Customer Delivery Download Files',
    },
    EXTERNAL_DELIVERY_DOWNLOAD_FILES: {
      path: 'external-delivery-download-files',
      name: 'External Delivery Download Files',
    },
  }),
  SYSTEM: buildNestedRoutes('/system', {
    PREFERENCES: {
      path: 'preferences',
      name: 'Preferences',
    },
    MANAGER: {
      path: 'manager',
      name: 'Manager',
    },
  }),
  FILE_DOWNLOAD: buildNestedRoutes('/', {
    CUSTOMER_DELIVERY_FILE: {
      path: 'onetime',
      name: 'Customer Delivery File',
    },
    EXTERNAL_DELIVERY_FILE: {
      path: 'share',
      name: 'External Delivery File',
    },
  }),
  NOTFOUND: { path: '*', name: 'Not Found' },
} as const;
